import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import { baseDeRutas } from './config/recursos';
import { AppShell } from './presentation/app/AppShell';
import { ENTRADAS } from './presentation/app/navegacion';
import { RequiereAnonimo, RequiereCapacidad, RequiereSesion } from './presentation/app/guardas';
import { Login } from './presentation/pages/auth/Login';
import { RecuperarClave } from './presentation/pages/auth/RecuperarClave';
import { CambiarClave } from './presentation/pages/auth/CambiarClave';
import { EnConstruccion } from './presentation/pages/EnConstruccion';
import { Alarmas } from './presentation/pages/alarmas/Alarmas';
import { CentroControl } from './presentation/pages/control/CentroControl';
import { Energia } from './presentation/pages/energia/Energia';
import { Panel } from './presentation/pages/panel/Panel';
import {
  UmbralesAnalogicos,
  UmbralesDigitales,
} from './presentation/pages/umbrales/UmbralesSensores';
import { UmbralesEnergia } from './presentation/pages/umbrales/UmbralesEnergia';
import { AlarmasEnergia } from './presentation/pages/umbrales/AlarmasEnergia';
import { Automatizacion } from './presentation/pages/automatizacion/Automatizacion';
import { Clientes } from './presentation/pages/inventario/Clientes';
import { Areas } from './presentation/pages/inventario/Areas';
import { Nodos } from './presentation/pages/inventario/Nodos';
import { Sensores } from './presentation/pages/inventario/Sensores';
import { Actuadores } from './presentation/pages/inventario/Actuadores';
import { TiposSensores } from './presentation/pages/catalogos/TiposSensores';
import { TiposActuadores } from './presentation/pages/catalogos/TiposActuadores';
import { Contactos } from './presentation/pages/notificaciones/Contactos';
import { Escalamiento } from './presentation/pages/notificaciones/Escalamiento';
import { Usuarios } from './presentation/pages/usuarios/Usuarios';

/*
 * El mapa arrastra MapLibre, que pesa tanto como el resto de la aplicación
 * junta. Se carga sólo al entrar en esa sección, para que quien no la use no
 * pague su descarga.
 */
const Vistas = lazy(() =>
  import('./presentation/pages/cctv/Vistas').then((m) => ({ default: m.Vistas })),
);

const Dvr = lazy(() => import('./presentation/pages/cctv/Dvr').then((m) => ({ default: m.Dvr })));

const Camaras = lazy(() =>
  import('./presentation/pages/camaras/Camaras').then((m) => ({ default: m.Camaras })),
);

const CuentasMedicion = lazy(() =>
  import('./presentation/pages/medidores/CuentasMedicion').then((m) => ({
    default: m.CuentasMedicion,
  })),
);

const Medidores = lazy(() =>
  import('./presentation/pages/medidores/Medidores').then((m) => ({ default: m.Medidores })),
);

const ReportesGenerados = lazy(() =>
  import('./presentation/pages/reportes/ReportesGenerados').then((m) => ({
    default: m.ReportesGenerados,
  })),
);

const Reportes = lazy(() =>
  import('./presentation/pages/reportes/Reportes').then((m) => ({ default: m.Reportes })),
);

const Bitacora = lazy(() =>
  import('./presentation/pages/bitacora/Bitacora').then((m) => ({ default: m.Bitacora })),
);

const Integraciones = lazy(() =>
  import('./presentation/pages/integraciones/Integraciones').then((m) => ({
    default: m.Integraciones,
  })),
);

const Envios = lazy(() =>
  import('./presentation/pages/envios/Envios').then((m) => ({ default: m.Envios })),
);

const Ubicaciones = lazy(() =>
  import('./presentation/pages/ubicaciones/Ubicaciones').then((m) => ({ default: m.Ubicaciones })),
);

const AlertasUbicacion = lazy(() =>
  import('./presentation/pages/ubicaciones/AlertasUbicacion').then((m) => ({
    default: m.AlertasUbicacion,
  })),
);
import { Monitoreo } from './presentation/pages/monitoreo/Monitoreo';
import { SistemaDiseno } from './presentation/pages/SistemaDiseno';
import { Cargando, EstadoError } from './presentation/components/ui/Estados';
import { useSesionStore } from './presentation/stores/sesionStore';
import { clienteConsultas } from './presentation/consultas';
import { servicioAutenticacion } from './services/ServicioAutenticacion';

/**
 * Pantallas ya migradas, por ruta.
 * Lo que no aparezca aquí muestra el marcador de sección en preparación, así
 * que la navegación completa funciona desde el primer día.
 */
const PANTALLAS: Record<string, React.ComponentType | undefined> = {
  '/alarmas': Alarmas,
  '/monitoreo': Monitoreo,
  '/control': CentroControl,
  '/energia': Energia,
  '/': Panel,
  '/umbrales-analogicos': UmbralesAnalogicos,
  '/umbrales-digitales': UmbralesDigitales,
  '/umbrales-energia': UmbralesEnergia,
  '/alarmas-energia': AlarmasEnergia,
  '/automatizacion': Automatizacion,
  '/clientes': Clientes,
  '/areas': Areas,
  '/nodos': Nodos,
  '/sensores': Sensores,
  '/actuadores': Actuadores,
  '/tipos-sensores': TiposSensores,
  '/tipos-actuadores': TiposActuadores,
  '/contactos': Contactos,
  '/escalamiento': Escalamiento,
  '/usuarios': Usuarios,
  '/ubicaciones': Ubicaciones,
  '/alertas-ubicacion': AlertasUbicacion,
  '/envios': Envios,
  '/integraciones': Integraciones,
  '/bitacora': Bitacora,
  '/reportes': Reportes,
  '/reportes-generados': ReportesGenerados,
  '/medidores': Medidores,
  '/cuentas-ekm': CuentasMedicion,
  '/camaras': Camaras,
  '/vistas': Vistas,
  '/dvr': Dvr,
};

export function App() {
  return (
    <QueryClientProvider client={clienteConsultas}>
      {/*
        `basename` permite servir la consola desde una subcarpeta sin tocar
        ninguna ruta: si está en app.veniot.net/Nuevo/, el enrutador entiende
        que /Nuevo/alarmas es su propia pantalla de alarmas.
      */}
      <BrowserRouter basename={baseDeRutas}>
        <Arranque />
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}

/**
 * Comprueba la compatibilidad con el servidor y recupera la sesión guardada
 * antes de dejar entrar a ninguna pantalla.
 */
function Arranque() {
  const arranque = useSesionStore((s) => s.arranque);
  const iniciar = useSesionStore((s) => s.iniciar);
  const descartarSesion = useSesionStore((s) => s.descartarSesion);

  useEffect(() => {
    void iniciar();
  }, [iniciar]);

  // Si la sesión se cierra desde otra pestaña o caduca en el servidor, esta
  // pestaña debe enterarse en vez de seguir mostrando datos obsoletos.
  useEffect(() => {
    return servicioAutenticacion.alCambiarSesion((haySesion) => {
      if (!haySesion) descartarSesion();
    });
  }, [descartarSesion]);

  if (arranque === 'comprobando') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', padding: 24 }}>
        <Cargando etiqueta="Verificando compatibilidad" />
      </div>
    );
  }

  if (arranque === 'incompatible') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', padding: 24 }}>
        <EstadoError
          titulo="Versión no compatible"
          descripcion="Esta versión de la consola no coincide con la del servidor. Recarga la página; si el aviso persiste, contacta al administrador."
        />
      </div>
    );
  }

  return <Rutas />;
}

function Rutas() {
  return (
    <Routes>
      <Route
        path="/acceso"
        element={
          <RequiereAnonimo>
            <Login />
          </RequiereAnonimo>
        }
      />

      <Route
        path="/recuperar-clave"
        element={
          <RequiereAnonimo>
            <RecuperarClave />
          </RequiereAnonimo>
        }
      />

      {/*
        El cambio de contraseña exige sesión pero no puede vivir dentro del
        armazón: mientras esté pendiente, el usuario no debe ver el menú ni
        ninguna otra sección.
      */}
      <Route
        path="/cambiar-clave"
        element={
          <RequiereSesion>
            <CambiarClave />
          </RequiereSesion>
        }
      />

      {/* La recuperación por correo devuelve al usuario aquí con la sesión ya abierta. */}
      <Route path="/restablecer-clave" element={<Navigate to="/cambiar-clave" replace />} />

      <Route
        element={
          <RequiereSesion>
            <AppShell />
          </RequiereSesion>
        }
      >
        {/*
          Cada entrada del menú genera su ruta con la misma capacidad que la
          hace visible: menú y permiso no pueden divergir. Las secciones que
          todavía no están migradas muestran un marcador.
        */}
        {ENTRADAS.map((entrada) => {
          const Pantalla = PANTALLAS[entrada.ruta];

          return (
            <Route
              key={entrada.ruta}
              path={entrada.ruta}
              element={
                <RequiereCapacidad capacidad={entrada.capacidad}>
                  <Suspense fallback={<Cargando etiqueta="Cargando sección" />}>
                    {Pantalla ? <Pantalla /> : <EnConstruccion titulo={entrada.etiqueta} />}
                  </Suspense>
                </RequiereCapacidad>
              }
            />
          );
        })}

        <Route path="/sistema-diseno" element={<SistemaDiseno />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
